import { useCallback, useEffect, useRef, useState } from "react";

import { isUserRejection } from "../services/relay.service";
import {
  PreMintGround,
  PreMintVerdictSignatureStatus,
  describeChainError,
  fetchPreMintVerdictSignatures,
  isPreMintChallenged,
  preMintVerdictSignatureErrorCopy,
  signAndSubmitPreMintVerdict,
  verdictEndpointUnavailable,
} from "../services/challenge.service";

// The server-collected lane for a pre-mint determination, the counterpart of useVerdictSignatures
// for Governance.judgePreMint. usePanelVerdict (paste signatures, submit from this wallet) is now
// only the fallback for when this endpoint cannot be reached.
//
//   GET  /pre-mint-challenges/:id/verdict-signatures   polled while `active`, grouped by ground
//   POST /pre-mint-challenges/:id/verdict-signatures   one judge's signature on one ground/verdict
//
// The pre-mint status endpoint carries no SEATED/DETERMINED trail to watch the way the post-mint
// one does (recordOutcome clears a state field; judgePreMint just clears the challenge outright),
// so `challenged` is read straight off chain (isChallenged) on the same cadence — the signal the
// dialog waits on to know the determination landed, however it got there.
//
// `unavailable` means the endpoint itself is down or missing (verdictEndpointUnavailable) — the
// caller switches to the paste flow. A refusal (400 / 403) is NOT unavailability: it is surfaced
// as `error` and the lane stays up.

// Same cadence as useVerdictSignatures — quick enough to feel live without hammering a public
// endpoint or the RPC.
const POLL_MS = 15000;

export const usePreMintVerdictSignatures = (requestId: bigint | null, active: boolean) => {
  const [status, setStatus] = useState<PreMintVerdictSignatureStatus | null>(null);
  const [challenged, setChallenged] = useState<boolean | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The request a read belongs to, so a slow response for a previous request never lands on the
  // current one.
  const currentId = useRef(requestId);
  currentId.current = requestId;

  const refresh = useCallback(async () => {
    if (requestId === null) {
      return;
    }
    const id = requestId;
    try {
      const [next, stillChallenged] = await Promise.all([
        fetchPreMintVerdictSignatures(id),
        isPreMintChallenged(id).catch(() => null),
      ]);
      if (mounted.current && currentId.current === id) {
        setStatus(next);
        setChallenged(stillChallenged);
        setUnavailable(false);
      }
    } catch (e: any) {
      if (mounted.current && currentId.current === id && verdictEndpointUnavailable(e)) {
        setUnavailable(true);
      }
    }
  }, [requestId]);

  // First read, then poll while active. A fresh request starts from a clean slate.
  useEffect(() => {
    setStatus(null);
    setChallenged(null);
    setUnavailable(false);
    setError(null);
    if (requestId === null || !active) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    refresh().finally(() => {
      if (!cancelled && mounted.current) {
        setLoading(false);
      }
    });
    const timer = window.setInterval(() => {
      if (!document.hidden) {
        refresh();
      }
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [requestId, active, refresh]);

  // One MetaMask typed-data prompt (no gas), then the POST. Resolves true once the server has
  // stored the signature; the tally it returns is folded into that ground straight away rather
  // than waiting for the next poll.
  const sign = useCallback(
    async (ground: PreMintGround, upheld: boolean): Promise<boolean> => {
      if (requestId === null) {
        return false;
      }
      setError(null);
      setSigning(true);
      try {
        const verdicts = await signAndSubmitPreMintVerdict(requestId, ground, upheld);
        if (mounted.current) {
          setStatus((prev) =>
            prev
              ? { ...prev, byGround: prev.byGround.map((g) => (g.ground === ground ? { ...g, verdicts } : g)) }
              : prev
          );
        }
        // The POST may have tipped a verdict over the threshold, and `challenged` is the chain's
        // to say once judgePreMint actually lands.
        refresh();
        return true;
      } catch (e: any) {
        if (!mounted.current || isUserRejection(e)) {
          return false;
        }
        if (verdictEndpointUnavailable(e) && e?.response !== undefined) {
          setUnavailable(true);
        }
        setError(preMintVerdictSignatureErrorCopy(e) ?? describeChainError(e));
        return false;
      } finally {
        if (mounted.current) {
          setSigning(false);
        }
      }
    },
    [requestId, refresh]
  );

  return { status, challenged, unavailable, loading, signing, error, sign, refresh };
};

export default usePreMintVerdictSignatures;
